import { Locale } from '@headless-workspace/config';
import { Repository, Result, ResultFailureFromApiError, ResultSuccess } from '@headless-workspace/core-domain';
import { RequestStrategy } from '@headless-workspace/data';
import { CustomerDTO } from './CustomerDTO';
import { CustomerValue, mapCustomer } from './CustomerValue';
import { SubscriptionsServiceType } from './SubscriptionsServiceType';

type ProductIdsType = {
    productId: string;
};
export interface CustomerRepositorySpec {
    getCustomerById(locale: Locale, customerId: string): Promise<Result<CustomerValue>>;

    deleteFavoriteStore(locale: Locale, customerId: string): Promise<Result<CustomerValue>>;

    subscribeToNewsletter(locale: Locale, email: string): Promise<Result<boolean>>;

    subscribeToNotifyMe(locale: Locale, email: string, products: ProductIdsType[]): Promise<Result<boolean>>;
}
export class CustomerRepository extends Repository implements CustomerRepositorySpec {
    path = '/customer/v1';

    constructor(requestStrategy: RequestStrategy) {
        super(requestStrategy.authStrategy, requestStrategy.refreshTokenStrategy, requestStrategy.queryParamStrategy);
    }

    async getCustomerById(locale: Locale, customerId: string): Promise<Result<CustomerValue>> {
        try {
            const userResponse = await this.datasource.getResource(`${this.path}/customers/${customerId}`, { locale });
            const userResult = CustomerDTO.parse(userResponse);

            return ResultSuccess(mapCustomer(userResult, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    private async subscriptionsToService(
        locale: Locale,
        email: string | undefined,
        subscriptionsServiceType: SubscriptionsServiceType,
        products?: ProductIdsType[],
    ): Promise<Result<boolean>> {
        try {
            await this.datasource.createResource(
                `${this.path}/subscriptions`,
                { email, action: 'subscribe', products },
                { type: subscriptionsServiceType, locale },
            );
            return ResultSuccess(true);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async subscribeToNewsletter(locale: Locale, email: string | undefined): Promise<Result<boolean>> {
        return await this.subscriptionsToService(locale, email, SubscriptionsServiceType.NEWSLETTER);
    }

    async subscribeToNotifyMe(
        locale: Locale,
        email: string | undefined,
        products: ProductIdsType[],
    ): Promise<Result<boolean>> {
        return await this.subscriptionsToService(locale, email, SubscriptionsServiceType.BACKTOSTORE, products);
    }

    async deleteFavoriteStore(locale: Locale, customerId: string): Promise<Result<CustomerValue>> {
        try {
            const userResponse = await this.datasource.patchResource(
                `${this.path}/customers/${customerId}`,
                { favoriteStoreId: '' },
                { locale },
            );
            const userResult = CustomerDTO.parse(userResponse);

            return ResultSuccess(mapCustomer(userResult, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
